/**
 * 替代品规则列表页
 * 展示替代品规则列表，支持筛选、新增、编辑、详情、启用/禁用、删除等操作
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  Table,
  Tag,
  Space,
  message,
  Modal,
  Form,
  Input,
  Select,
  Col,
} from 'antd';
import { ExclamationCircleOutlined, PlusOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import dayjs from 'dayjs';
import FilterPanel from '../../components/common/FilterPanel';
import StoreSelect from '../../components/common/StoreSelect';
import type { SubstituteRuleDTO, QuerySubstituteRulePageRequest } from '../../types/promotion-rule/substituteRule';
import type { Paging } from '../../types/common';
import * as substituteRuleService from '../../services/promotion-rule/substituteRuleService';
import {
  SubstituteRuleStatus,
  SubstituteRuleStatusOptions,
} from '../../constants/promotionRule';
import SubstituteRuleDrawer from './components/SubstituteRuleDrawer';
import SubstituteRuleDetail from './components/SubstituteRuleDetail';

const SubstituteRuleList: React.FC = () => {
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<SubstituteRuleDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({ currentPage: 1, pageSize: 10, totalCount: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Drawer states
  const [formVisible, setFormVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [currentRecord, setCurrentRecord] = useState<SubstituteRuleDTO | null>(null);
  const [detailRecord, setDetailRecord] = useState<SubstituteRuleDTO | null>(null);

  // Load data
  const loadData = async (params: QuerySubstituteRulePageRequest) => {
    setLoading(true);
    try {
      const response = await substituteRuleService.pageSubstituteRule(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Initialize
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 10 });
  }, []);

  // Search
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    const params: QuerySubstituteRulePageRequest = {
      currentPage: 1,
      pageSize: pagination.pageSize,
      applyStoreCode: values.applyStoreCode,
      ruleCode: values.ruleCode,
      ruleName: values.ruleName,
      status: values.status,
    };
    loadData(params);
  };

  // Reset
  const handleReset = () => {
    searchForm.resetFields();
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  // Pagination change
  const handlePageChange = (page: number, pageSize: number) => {
    const values = searchForm.getFieldsValue();
    const params: QuerySubstituteRulePageRequest = {
      currentPage: page,
      pageSize,
      applyStoreCode: values.applyStoreCode,
      ruleCode: values.ruleCode,
      ruleName: values.ruleName,
      status: values.status,
    };
    loadData(params);
  };

  // Create
  const handleCreate = () => {
    setCurrentRecord(null);
    setDrawerMode('create');
    setFormVisible(true);
  };

  // Edit - 重新查询详情以获取完整数据（含 matchConfigs）
  const handleEdit = async (record: SubstituteRuleDTO) => {
    try {
      const response = await substituteRuleService.getSubstituteRuleDetail(record.ruleCode);
      if (response.success && response.data) {
        setCurrentRecord(response.data);
        setDrawerMode('edit');
        setFormVisible(true);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    }
  };

  // View detail
  const handleViewDetail = (record: SubstituteRuleDTO) => {
    setDetailRecord(record);
    setDetailVisible(true);
  };

  // Enable (batch support)
  const handleEnableBatch = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('common.message.pleaseSelectData'));
      return;
    }
    Modal.confirm({
      title: t('promotionRule.confirm.batchEnable.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('promotionRule.confirm.batchEnable.content', { count: selectedRowKeys.length }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          for (const ruleCode of selectedRowKeys as string[]) {
            await substituteRuleService.enableSubstituteRule({ ruleCode });
          }
          message.success(t('promotionRule.message.batchEnableSuccess'));
          setSelectedRowKeys([]);
          loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };

  // Disable (batch support)
  const handleDisableBatch = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('common.message.pleaseSelectData'));
      return;
    }
    Modal.confirm({
      title: t('promotionRule.confirm.batchDisable.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('promotionRule.confirm.batchDisable.content', { count: selectedRowKeys.length }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          for (const ruleCode of selectedRowKeys as string[]) {
            await substituteRuleService.disableSubstituteRule({ ruleCode });
          }
          message.success(t('promotionRule.message.batchDisableSuccess'));
          setSelectedRowKeys([]);
          loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };

  // Batch delete
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('common.message.pleaseSelectData'));
      return;
    }

    Modal.confirm({
      title: t('promotionRule.confirm.batchDelete.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('promotionRule.confirm.batchDelete.content', { count: selectedRowKeys.length }),
      okText: t('common.confirm'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const ruleCodes = selectedRowKeys as string[];
          const response = await substituteRuleService.batchDeleteSubstituteRules({ ruleCodes });
          if (response.success) {
            message.success(t('promotionRule.message.deleteSuccess'));
            setSelectedRowKeys([]);
            loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
          } else {
            message.error(response.errorMessage || t('common.message.operationFailed'));
          }
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };

  // Form success callback
  const handleFormSuccess = () => {
    setFormVisible(false);
    loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
  };

  // Format datetime
  const formatDateTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('DD-MM-YYYY HH:mm:ss') : '-';
  };

  // Render status tag
  const renderStatusTag = (status: SubstituteRuleStatus) => {
    const option = SubstituteRuleStatusOptions.find(opt => opt.value === status);
    if (!option) return status;
    return <Tag color={option.color}>{t(option.label)}</Tag>;
  };

  // Delete single record
  const handleDelete = (record: SubstituteRuleDTO) => {
    Modal.confirm({
      title: t('promotionRule.confirm.delete.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('promotionRule.confirm.delete.content', { ruleName: record.ruleName }),
      okText: t('common.confirm'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await substituteRuleService.deleteSubstituteRule({ ruleCode: record.ruleCode });
          if (response.success) {
            message.success(t('promotionRule.message.deleteSuccess'));
            loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
          } else {
            message.error(response.errorMessage || t('common.message.operationFailed'));
          }
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };


  // Table columns
  const columns = [
    {
      title: t('promotionRule.list.table.ruleCode'),
      dataIndex: 'ruleCode',
      key: 'ruleCode',
      width: 140,
      fixed: 'left' as const,
      render: (text: string, record: SubstituteRuleDTO) => (
        <a onClick={() => handleViewDetail(record)}>{text}</a>
      ),
    },
    {
      title: t('promotionRule.list.table.ruleName'),
      dataIndex: 'ruleName',
      key: 'ruleName',
      width: 200,
    },
    {
      title: t('promotionRule.list.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatusTag,
    },
    {
      title: t('promotionRule.list.table.effectiveTime'),
      key: 'effectiveTime',
      width: 200,
      render: (_: any, record: SubstituteRuleDTO) => {
        const start = record.effectiveStartTime ? dayjs(record.effectiveStartTime).format('DD-MM-YYYY') : '-';
        const end = record.effectiveEndTime ? dayjs(record.effectiveEndTime).format('DD-MM-YYYY') : t('common.permanent');
        return `${start} ~ ${end}`;
      },
    },
    {
      title: t('promotionRule.list.table.gmtModified'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 180,
      render: formatDateTime,
    },
    {
      title: t('promotionRule.list.table.actions'),
      key: 'actions',
      width: 160,
      fixed: 'right' as const,
      render: (_: any, record: SubstituteRuleDTO) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            {t('promotionRule.list.actions.edit')}
          </Button>
          <Button type="link" size="small" danger onClick={() => handleDelete(record)}>
            {t('promotionRule.list.actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  // Search form fields
  const renderSearchFields = () => (
    <>
      <Col span={6}>
        <Form.Item name="applyStoreCode" label="Store">
          <StoreSelect placeholder="Search store code / name" />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="ruleCode" label={t('promotionRule.list.search.ruleCode')}>
          <Input
            placeholder={t('promotionRule.list.search.ruleCode')}
            allowClear
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="ruleName" label={t('promotionRule.list.search.ruleName')}>
          <Input
            placeholder={t('promotionRule.list.search.ruleName')}
            allowClear
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="status" label={t('promotionRule.list.search.status')}>
          <Select
            placeholder={t('promotionRule.list.search.status')}
            allowClear
            options={SubstituteRuleStatusOptions.map(opt => ({
              label: t(opt.label),
              value: opt.value,
            }))}
          />
        </Form.Item>
      </Col>
    </>
  );

  // Row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Search Panel */}
      <FilterPanel
        form={searchForm}
        onFilter={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        {renderSearchFields()}
      </FilterPanel>

      {/* Action Bar */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Space>
          <Button danger onClick={handleBatchDelete}>
            {t('promotionRule.list.actions.batchDelete')}
          </Button>
          <Button type="primary" onClick={handleEnableBatch}>
            {t('promotionRule.list.actions.enable')}
          </Button>
          <Button onClick={handleDisableBatch}>
            {t('promotionRule.list.actions.disable')}
          </Button>
          {selectedRowKeys.length > 0 && (
            <span style={{ marginLeft: 8 }}>
              {t('picking.pickingTask.list.selection.selected', { count: selectedRowKeys.length })}
            </span>
          )}
        </Space>
        <Space>
          <Button type="primary" onClick={handleCreate}>
            {t('promotionRule.list.actions.create')}
          </Button>
        </Space>
      </div>

      {/* Table */}
      <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="ruleCode"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1600, y: scrollY }}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: pagination.totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('common.pagination.total', { total }),
            onChange: handlePageChange,
          }}
        />
      </div>

      {/* Form Drawer */}
      {formVisible && (
        <SubstituteRuleDrawer
          visible={formVisible}
          mode={drawerMode}
          record={currentRecord}
          onClose={() => setFormVisible(false)}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Detail Drawer */}
      {detailVisible && detailRecord && (
        <SubstituteRuleDetail
          visible={detailVisible}
          record={detailRecord}
          onClose={() => setDetailVisible(false)}
          onEdit={() => {
            setDetailVisible(false);
            if (detailRecord) {
              handleEdit(detailRecord);
            }
          }}
        />
      )}
    </div>
  );
};

export default SubstituteRuleList;
